import { Injectable } from '@angular/core';
import { formatNumber, formatPercent } from '@angular/common';
import { forEach } from 'lodash-es';
import {
  DataGridElement,
  DataResponse,
  DataRowData,
  FieldDefinition,
  FieldDefinitions,
  Format,
  IFormat,
  IFormats,
  Type
} from '@portal/app/shared/types';
import { CustomCurrencyPipe } from '@portal/app/shared/pipes/custom-currency.pipe';
import { CurrencyService } from '@portal/app/shared/services/currency.service';
import { CurrencySymbolValue } from '@portal/app/shared/types/currency-format';
import { DateTimeService } from '@portal/app/shared/services/date-time.service';
import { PortfolioCustomMetricDTO } from '@libs/api';

declare global {
  interface Window {
    formatNumber: unknown;
  }
}

window.formatNumber = formatNumber;

@Injectable({
  providedIn: 'root'
})
export class FormatterService {
  public currencySymbolValue: CurrencySymbolValue | null = '$';

  public static fieldGenerator = (
    format: Format,
    digitsInfo: string | null = '1.0-0',
    type: Type = 'double'
  ): FieldDefinition => {
    return {
      id: -1,
      dashboardId: -1,
      literalId: 'testField',
      label: 'test field',
      format,
      type,
      digitsInfo,
      aggregateFunction: 'none'
    };
  };

  constructor(
    public currencyService: CurrencyService,
    public customCurrencyPipe: CustomCurrencyPipe
  ) {
    // GET THE CURRENCY SYMBOL($, €, £ etc...)
    const transformedValue = this.customCurrencyPipe.transform(1, '1.0-0');
    if (transformedValue) {
      // strip the symbol
      this.currencySymbolValue = transformedValue.slice(
        0,
        -1
      ) as CurrencySymbolValue;
    }
  }

  validateAndReplace(
    value: string | number,
    replaceWith: string | number
  ): string | number {
    if (
      value == null ||
      value === 'NaN' ||
      value === 'Infinity' ||
      value === '-Infinity' ||
      value === ''
    ) {
      return replaceWith;
    }
    return value;
  }

  private isInvalidValue(value: number | string | null): boolean {
    return (
      value == null ||
      value === 'NaN' ||
      value === 'Infinity' ||
      value === '-Infinity'
    );
  }

  private isNonFiniteNumber(value: number | string | null): boolean {
    return value != null && typeof value === 'number' && !isFinite(value);
  }

  /**
   * `lowerBoundValue` is conventionally a display-scale number (e.g. 0.01 means
   * "show '< 0.01%' for percent" or "show '< $0.01' for currency"). Comparison
   * must happen in display scale. For percent, `format()` is called with raw
   * fractional values (0.0001 = 0.01%), so callers in that path pass
   * `valueIsDisplayScale: false` and the value is multiplied by 100 before
   * comparison. `formatPartialValue()` runs after `convertPartialItem` produced
   * display-scale values, so the default `true` applies.
   */
  private isAtOrBelowLowerBound(
    value: number | string | null,
    field: FieldDefinition | IFormat | undefined | null,
    valueIsDisplayScale = true
  ): boolean {
    if (field == null) return false;
    if (typeof value !== 'number' || value === 0) return false;
    if (!('lowerBoundValue' in field) || field.lowerBoundValue == null)
      return false;
    const comparable =
      field.format === 'percent' && !valueIsDisplayScale
        ? Math.abs(value * 100)
        : Math.abs(value);
    return comparable <= field.lowerBoundValue;
  }

  private formatLowerBoundDisplay(field: FieldDefinition): {
    value: number;
    digitsInfo: string;
    symbol: '<';
  } {
    return {
      value: field.lowerBoundValue as number,
      digitsInfo: field.lowerBoundDigitsInfo ?? field.digitsInfo ?? '',
      symbol: '<'
    };
  }

  private formatCurrencyValue(
    value: number | string | null,
    digitsInfo?: string | null
  ): string {
    return this.customCurrencyPipe.transform(value, digitsInfo || '') || '';
  }

  private formatPercentValue(
    value: number | string | null,
    digitsInfo?: string | null
  ): string {
    return value !== ''
      ? `${formatNumber(parseFloat(`${value}`), 'en-US', digitsInfo || '')}%`
      : '-';
  }

  private formatNumberValue(
    value: number | string | null,
    digitsInfo?: string | null
  ): string {
    return formatNumber(parseFloat(`${value}`), 'en-US', digitsInfo || '');
  }

  private formatDateValue(value: number | string | null): string {
    return DateTimeService.dateWithoutTimeZone(value).toLocaleString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  /**
   * Formats a given value based on the specified field definition and optional parameters.
   *
   * @param field - The field definition containing formatting options.
   * @param value - The value to be formatted.
   * @param [removeNegativeSign=false] - Optional parameter to remove the negative sign from negative values.
   * @returns - The formatted value as a string.
   */
  format(
    field: FieldDefinition,
    value: number | string,
    removeNegativeSign = false
  ): string {
    if (
      this.isInvalidValue(value) ||
      value === '' ||
      this.isNonFiniteNumber(value)
    ) {
      return '-';
    }

    let transformedValue = value;
    if (
      removeNegativeSign &&
      typeof transformedValue === 'number' &&
      transformedValue < 0
    ) {
      transformedValue = Math.abs(transformedValue);
    }

    let fieldDigitsInfo = field.digitsInfo || '';
    let lowerSymbol = '';
    let boundTriggered = false;

    if (this.isAtOrBelowLowerBound(transformedValue, field, false)) {
      const lowerBoundInfo = this.formatLowerBoundDisplay(field);
      fieldDigitsInfo = lowerBoundInfo.digitsInfo || '';
      lowerSymbol = lowerBoundInfo.symbol;
      transformedValue = lowerBoundInfo.value;
      boundTriggered = true;
    }

    const prefix = lowerSymbol ? `${lowerSymbol} ` : '';

    switch (field.format) {
      case 'currency':
        return `${prefix}${this.formatCurrencyValue(transformedValue, fieldDigitsInfo)}`;
      case 'percent':
        return `${prefix}${
          boundTriggered
            ? this.formatPercentValue(transformedValue, fieldDigitsInfo)
            : formatPercent(
                transformedValue as number,
                'en-US',
                fieldDigitsInfo
              )
        }`;
      case 'number':
        return `${prefix}${formatNumber(
          transformedValue as number,
          'en-US',
          fieldDigitsInfo
        )}`;
      case 'yyyy-MM-dd':
      case 'UTC-yyyy-MM-dd':
        return this.formatDateValue(transformedValue);
      case 'days':
        return `${transformedValue} Days`;
      default:
        return `${transformedValue}`;
    }
  }

  formatPartialValue(
    field: FieldDefinition | IFormat | undefined | null,
    value: number | string | null
  ): string {
    if (this.isInvalidValue(value) && field?.format !== 'empty') {
      return '-';
    } else if (this.isNonFiniteNumber(value)) {
      return '-';
    }

    let fieldDigitsInfo = field?.digitsInfo ?? '';
    let lowerSymbol = '';
    let transformedValue = value;

    if (this.isAtOrBelowLowerBound(value, field, true)) {
      const lowerBoundInfo = this.formatLowerBoundDisplay(
        field as FieldDefinition
      );
      fieldDigitsInfo = lowerBoundInfo.digitsInfo || '';
      lowerSymbol = lowerBoundInfo.symbol;
      transformedValue = lowerBoundInfo.value;
    }

    const prefix = lowerSymbol ? `${lowerSymbol} ` : '';

    switch (field?.format) {
      case 'currency':
        return `${prefix}${this.formatCurrencyValue(transformedValue, fieldDigitsInfo)}`;
      case 'percent':
        return `${prefix}${this.formatPercentValue(transformedValue, fieldDigitsInfo)}`;
      case 'number':
        return this.formatNumberValue(value, field?.digitsInfo);
      case 'yyyy-MM-dd':
      case 'UTC-yyyy-MM-dd':
        return this.formatDateValue(value);
      case 'empty':
        return '';
      default:
        return `${value}`;
    }
  }

  formatForExport(
    field: FieldDefinition | IFormat,
    value: number | string | null
  ): string {
    if (this.isInvalidValue(value) || this.isNonFiniteNumber(value)) {
      return '-';
    }
    switch (field?.format) {
      case 'currency':
        return this.formatCurrencyValue(value, field.digitsInfo);
      case 'percent':
        return this.formatPercentValue(value, field.digitsInfo);
      case 'number':
        return this.formatNumberValue(value, field.digitsInfo);
      case 'yyyy-MM-dd':
      case 'UTC-yyyy-MM-dd':
        return this.formatDateValue(value);
      case 'empty':
        return '';
      default:
        return `${value}`;
    }
  }

  formatDataPartially(
    fields: FieldDefinitions | IFormats,
    data: DataResponse[]
  ): DataResponse[] {
    return data.map((item: DataResponse) => {
      for (const key in item) {
        if (Object.prototype.hasOwnProperty.call(item, key) && fields[key]) {
          item[key] = this.convertPartialItem(
            fields[key] as FieldDefinition | IFormat,
            item[key] as string | number | null
          );
        }
      }
      return item;
    });
  }

  checkObjectEquality(obj1: DataResponse, obj2: DataResponse) {
    const obj1Length = Object.keys(obj1).length;
    const obj2Length = Object.keys(obj2).length;
    if (obj1Length === obj2Length) {
      return Object.keys(obj1).every((key) => {
        if (Object.keys(obj2).includes(key) && obj2[key] === obj1[key]) {
          return true;
        }
      });
    }
    return false;
  }

  groupByDimensions(
    element: DataGridElement,
    tableData: DataResponse[]
  ): DataResponse[] {
    // collect a list of dimension ids
    const dimensionsIds: string[] = [];
    forEach(element.selected, (col) => {
      if (col.type === 'DIMENSION') {
        dimensionsIds.push(col.dashboardFieldId);
      }
    });

    const presentCombos: DataResponse[] = [];
    forEach(tableData, (row) => {
      const rowDimensionCombo: DataResponse = {};
      forEach(dimensionsIds, (did) => {
        if (row[did]) {
          rowDimensionCombo[did] = row[did] as DataRowData;
        }
      });
      presentCombos.push(rowDimensionCombo);
    });
    // MAKE THE AVAILABLE DIMENSION COMBOS
    const uniqueCombos: DataResponse[] = [];
    forEach(presentCombos, (combo) => {
      if (uniqueCombos.length) {
        const avail: DataResponse = uniqueCombos.find((ucombo) => {
          if (this.checkObjectEquality(ucombo, combo)) {
            return combo;
          }
        }) as DataResponse;
        if (!avail) {
          uniqueCombos.push(combo);
        }
      } else {
        uniqueCombos.push(combo);
      }
    });

    //ASSIGN PIVOT DATA VALUES TO UNIQUE DIMENSION COMBOS
    forEach(tableData, (row) => {
      const rowDimensionCombo: DataResponse = {};
      forEach(dimensionsIds, (did) => {
        if (row[did]) {
          rowDimensionCombo[did] = row[did] as DataRowData;
        }
      });
      let comboMatchIndex = -1;
      let comboMatch: DataResponse = {};
      forEach(uniqueCombos, (ucombo, index) => {
        if (
          dimensionsIds.every(
            (id) =>
              Object.keys(rowDimensionCombo).includes(id) &&
              rowDimensionCombo[id] === ucombo[id]
          )
        ) {
          comboMatchIndex = index;
          comboMatch = row;
        }
      });
      if (comboMatch && comboMatchIndex !== -1) {
        uniqueCombos[comboMatchIndex] = Object.assign(
          {},
          uniqueCombos[comboMatchIndex],
          comboMatch
        );
      }
    });
    return uniqueCombos;
  }

  formatDataResponsePartially(
    fields: FieldDefinitions | IFormats,
    item: DataResponse
  ): DataResponse {
    for (const key in item) {
      if (Object.prototype.hasOwnProperty.call(item, key) && fields[key]) {
        item[key] = this.convertPartialItem(
          fields[key] as FieldDefinition | IFormat,
          item[key] as DataRowData
        );
      }
    }
    return item;
  }

  convertPartialItem(
    field: FieldDefinition | IFormat,
    value: number | string | null
  ) {
    if (
      value == null ||
      value === 'NaN' ||
      value === 'Infinity' ||
      value === '-Infinity'
    ) {
      return value;
    } else if (value != null && typeof value === 'number' && !isFinite(value)) {
      return value;
    }
    switch (field.format) {
      case 'currency':
      case 'number': {
        const val = parseFloat(
          formatNumber(
            value as number,
            'en-US',
            field.digitsInfo || ''
          ).replace(/,/g, '')
        );
        if (val === 0 && (value as number) !== 0) {
          return value;
        }
        return Number.isFinite(val) ? val : 0;
      }
      case 'percent': {
        const val = parseFloat(
          formatPercent(
            value as number,
            'en-US',
            field.digitsInfo || ''
          ).replace(/,/g, '')
        );
        if (val === 0 && (value as number) !== 0) {
          return value;
        }
        return Number.isFinite(val) ? val : 0;
      }
      default:
        return value;
    }
  }

  resolveCustomMetricFormat(
    metric: PortfolioCustomMetricDTO,
    fieldDefinitions: FieldDefinitions
  ): Format {
    const dependentLiteralIds = metric.metricsList ?? [];
    if (
      dependentLiteralIds.some(
        (id) => fieldDefinitions[id]?.format === 'percent'
      )
    ) {
      return 'percent';
    }
    if (
      dependentLiteralIds.some(
        (id) => fieldDefinitions[id]?.format === 'currency'
      )
    ) {
      return 'currency';
    }
    return 'number';
  }

  resolveCustomMetricDigitsInfo(
    metric: PortfolioCustomMetricDTO,
    fieldDefinitions: FieldDefinitions
  ): string {
    const firstLiteralId = metric.metricsList?.[0];
    return fieldDefinitions[firstLiteralId ?? '']?.digitsInfo ?? '1.0-2';
  }
}
